<template>
  <header class="header-top">
    <nav class="navbar navbar-expand-lg">
      <div class="navbar-left">
        <div class="logo-wrapper">
          <v-img
            contain
            width="175"
            src="img/jcom_logo.jpg"
            alt="JCOM Logo"
            class="logo-image"
          />
        </div>
        <div class="menu-divider"></div>
        <top-menu />
      </div>
      <v-spacer />
      <!-- ends: navbar-left -->
      <div class="navbar-right">
        <ul class="navbar-right__menu">
          <li class="nav-author">
            <user-menu />
          </li>
          <li class="nav-logout">
            <v-btn
              class="logout-btn"
              @click.prevent="logoutUser"
              variant="text"
              :loading="logoutLoading"
            >
              <v-icon left> mdi-logout </v-icon>
              <span class="logout-text text-dark">Logout</span>
            </v-btn>
          </li>
          <li class="nav-mobile-menu">
            <mobile-menu />
          </li>
          <!-- ends: .nav-author -->
        </ul>
        <!-- ends: .navbar-right__menu -->
      </div>
      <!-- ends: .navbar-right -->
    </nav>
  </header>
</template>

<script>
import TopMenu from "@/components/TopMenu.vue";
import NavMessage from "@/components/NavMessage.vue";
import NavNotification from "@/components/NavNotification.vue";
import NavSettings from "@/components/NavSettings.vue";
import NavFlag from "@/components/NavFlag.vue";
import UserMenu from "@/components/UserMenu.vue";
import MobileMenu from "@/components/MobileMenu.vue";
import common from "@/Common.vue";

export default {
  mixins: [common],
  components: {
    TopMenu,
    NavMessage,
    NavNotification,
    NavSettings,
    NavFlag,
    UserMenu,
    MobileMenu,
  },
  data() {
    return {
      logoutLoading: false,
    };
  },
  methods: {
    async logoutUserWithLoader() {
      this.logoutLoading = true;
      try {
        await this.logoutUser();
      } finally {
        this.logoutLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/* Header Container */
.header-top {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.2);
  position: sticky;
  top: 0;
  z-index: 1000;
  animation: slideDownFade 0.6s ease-out;
}

/* Navbar */
.navbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  gap: 20px;
  flex-wrap: wrap;

  @media (max-width: 959px) {
    padding: 12px 16px;
  }

  @media (max-width: 599px) {
    padding: 12px 12px;
  }
}

/* Navbar Left */
.navbar-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 0 0 auto;

  @media (max-width: 599px) {
    gap: 12px;
  }
}

/* Logo Wrapper */
.logo-wrapper {
  display: flex;
  align-items: center;
  transition: all 300ms cubic-bezier(0.2, 0.8, 0.2, 1);
  cursor: pointer;

  &:hover {
    transform: scale(1.05);
    filter: brightness(1.1);
  }

  .logo-image {
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transition: all 300ms cubic-bezier(0.2, 0.8, 0.2, 1);

    &:hover {
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
    }
  }
}

/* Menu Divider */
.menu-divider {
  width: 2px;
  height: 40px;
  background: linear-gradient(
    180deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  border-radius: 1px;

  @media (max-width: 959px) {
    height: 32px;
  }

  @media (max-width: 599px) {
    height: 28px;
  }
}

/* Navbar Right */
.navbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;

  @media (max-width: 599px) {
    gap: 4px;
  }
}

/* Navbar Menu */
.navbar-right__menu {
  display: flex;
  align-items: center;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 12px;

  @media (max-width: 599px) {
    gap: 6px;
  }

  li {
    transition: all 260ms cubic-bezier(0.2, 0.8, 0.2, 1);

    &:hover {
      transform: translateY(-2px);
    }
  }
}

/* Navigation Item - Author */
.nav-author {
  display: flex;
  align-items: center;

  @media (max-width: 599px) {
    display: none;
  }
}

/* Navigation Item - Logout */
.nav-logout {
  display: flex;
  align-items: center;

  @media (max-width: 718px) {
    display: none;
  }
}

/* Logout Button */
.logout-btn {
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.15),
    rgba(255, 255, 255, 0.05)
  ) !important;
  color: blueviolet !important;
  border-radius: 8px !important;
  padding: 8px 16px !important;
  font-weight: 700;
  letter-spacing: 0.5px;
  font-size: 13px;
  text-transform: uppercase;
  transition: all 280ms cubic-bezier(0.2, 0.8, 0.2, 1);
  position: relative;
  overflow: hidden;
  border: 2px solid rgba(255, 255, 255, 0.2) !important;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 130px;
  justify-content: center;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 500ms ease;
  }

  &:hover:not(:disabled)::before {
    left: 100%;
  }

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.25) !important;
    border-color: rgba(255, 255, 255, 0.4) !important;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    transform: translateY(-2px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.8;
  }

  .logout-text {
    display: inline-block;
    transition: all 300ms ease;
  }

  :deep(.v-btn__content) {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  :deep(.v-icon) {
    font-size: 20px;
    transition: all 300ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }

  &:hover:not(:disabled) :deep(.v-icon) {
    transform: rotate(-15deg) scale(1.1);
  }

  :deep(.v-progress-circular) {
    margin-right: 4px !important;
  }
}

/* Mobile Menu */
.nav-mobile-menu {
  display: none;

  @media (max-width: 718px) {
    display: flex;
    align-items: center;
  }
}

/* Animations */
@keyframes slideDownFade {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-4px);
  }
}

/* Responsive Adjustments */
@media only screen and (max-width: 600px) {
  .navbar {
    flex-direction: column;
    padding: 12px;
    gap: 12px;

    .navbar-left {
      width: 100%;
      justify-content: space-between;
    }

    .navbar-right {
      width: 100%;
      justify-content: flex-end;
    }
  }

  .logo-wrapper {
    flex-shrink: 0;
  }

  .logout-btn {
    font-size: 12px;
    padding: 6px 12px !important;
    min-width: 110px;
  }
}

@media only screen and (min-width: 601px) and (max-width: 718px) {
  .navbar {
    padding: 12px 16px;
  }

  .logo-wrapper {
    flex-shrink: 0;

    :deep(.v-img) {
      width: 150px !important;
    }
  }

  .logout-btn {
    font-size: 12px;
    padding: 8px 14px !important;
    min-width: 120px;
  }
}

@media only screen and (min-width: 719px) and (max-width: 959px) {
  .navbar {
    padding: 12px 20px;
  }

  .logout-btn {
    font-size: 12px;
    padding: 8px 16px !important;
    min-width: 125px;
  }
}
</style>
